import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import { useTestLora } from '../../api/queries/lora';

interface LoraTestModalProps {
  open: boolean;
  onClose: () => void;
  triggerWord: string;
  entityName: string;
  entityId: string;
  loraModelId: string;
}

const PRESET_PROMPTS = [
  { value: 'neutral', label: 'Neutral portrait (front)' },
  { value: 'three-quarter', label: 'Three-quarter view' },
  { value: 'profile', label: 'Profile view' },
  { value: 'determined', label: 'Emotional — determined' },
  { value: 'urban', label: 'Scene — urban environment' },
  { value: 'closeup', label: 'High detail close-up' },
  { value: 'fullbody', label: 'Full body — standing' },
  { value: 'custom', label: 'Custom prompt' },
];

const PRESET_TEMPLATES: Record<string, string> = {
  neutral:
    '{trigger}, portrait, neutral expression, front facing, soft natural daylight, close portrait, photorealistic, sharp focus, neutral background',
  'three-quarter':
    '{trigger}, portrait, three-quarter view, neutral expression, soft natural daylight, bust shot, photorealistic, sharp focus, neutral background',
  profile:
    '{trigger}, portrait, profile view, serious expression, indoor ambient light, close portrait, photorealistic, sharp focus, neutral background',
  determined:
    '{trigger}, portrait, determined expression, slight low angle, dramatic side lighting, bust shot, photorealistic, sharp focus',
  urban:
    '{trigger}, portrait, standing in a near-future urban alley, dim artificial lighting, three-quarter view, photorealistic, cinematic depth of field',
  closeup:
    '{trigger}, extreme close portrait, sharp focus on facial features, skin texture detail, soft natural daylight, photorealistic, 8k detail',
  fullbody:
    '{trigger}, full body portrait, standing, neutral background, photorealistic, sharp focus',
};

const ASPECT_RATIOS = [
  { value: '1:1', label: 'Square (1:1)' },
  { value: '3:4', label: 'Portrait (3:4)' },
  { value: '4:3', label: 'Landscape (4:3)' },
  { value: '16:9', label: 'Widescreen (16:9)' },
  { value: '9:16', label: 'Vertical (9:16)' },
];

export function LoraTestModal({
  open,
  onClose,
  triggerWord,
  entityName,
  entityId,
  loraModelId,
}: LoraTestModalProps) {
  const [preset, setPreset] = useState('neutral');
  const [customPrompt, setCustomPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [history, setHistory] = useState<
    Array<{ url: string; prompt: string }>
  >([]);

  const test = useTestLora(entityId);

  const resolvedPrompt =
    preset === 'custom'
      ? customPrompt
      : (PRESET_TEMPLATES[preset] ?? '').replace('{trigger}', triggerWord);

  const handleGenerate = async () => {
    if (!resolvedPrompt.trim()) return;
    try {
      const result = await test.mutateAsync({
        loraId: loraModelId,
        prompt: resolvedPrompt,
        aspectRatio,
      });
      setHistory((prev) => [
        { url: result.imageUrl, prompt: result.prompt },
        ...prev,
      ]);
    } catch {
      // Error rendered below from test.error
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Test LoRA — {entityName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1}>
            <Box sx={{ flex: 1 }}>
              <FilterableSelect
                options={PRESET_PROMPTS}
                value={preset}
                onChange={setPreset}
                label="Prompt preset"
                allowEmpty={false}
              />
            </Box>
            <Box sx={{ width: 200 }}>
              <FilterableSelect
                options={ASPECT_RATIOS}
                value={aspectRatio}
                onChange={setAspectRatio}
                label="Aspect"
                allowEmpty={false}
              />
            </Box>
          </Stack>

          <TextField
            label="Prompt"
            multiline
            rows={3}
            value={preset === 'custom' ? customPrompt : resolvedPrompt}
            onChange={(e) => {
              if (preset === 'custom') setCustomPrompt(e.target.value);
            }}
            disabled={preset !== 'custom'}
            helperText={`Trigger word: ${triggerWord}`}
            size="small"
          />

          {test.isPending && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {test.isError && (
            <Alert severity="error">
              {test.error instanceof Error
                ? test.error.message
                : 'Test failed. The LoRA inference provider is likely not configured (set FalAi:ApiKey on the API).'}
            </Alert>
          )}

          {history.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Results</Typography>
              {history.map((h, i) => (
                <Box key={`${h.url}-${i}`}>
                  <img
                    src={h.url}
                    alt={`Generated ${i + 1}`}
                    style={{ maxWidth: '100%', borderRadius: 8 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      fontFamily: 'monospace',
                      fontSize: 11,
                    }}
                  >
                    {h.prompt}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}

          {!test.isPending && history.length === 0 && !test.isError && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 2 }}
            >
              Select a preset or write a custom prompt, then click Generate.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={test.isPending || !resolvedPrompt.trim()}
        >
          {test.isPending ? 'Generating...' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
